import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { FilterChip } from './FilterChip';

export interface ActiveFilter {
  key: string;
  label: string;
  onClear: () => void;
}

export interface FilterBarProps {
  children: ReactNode;
  activeFilters?: ActiveFilter[];
  onClearAll?: () => void;
  className?: string;
  /**
   * `card` (default) — standalone white card, for pages that don't host their
   * filters inside a DataTable shell.
   * `inset` — borderless strip for the DataTable `toolbar` slot. The parent
   * card provides the chrome; this just lays controls out flush with padding
   * and adds a bottom divider when chips are shown.
   */
  variant?: 'card' | 'inset';
  /**
   * `flex` (default) — `flex flex-wrap` layout. Good for 3–4 controls.
   * `grid` — responsive grid (1 col → 2 cols → 4 cols) that keeps 5+ filters
   * aligned instead of letting them wrap at arbitrary points.
   */
  layout?: 'flex' | 'grid';
}

const LAYOUT_CLASSES: Record<NonNullable<FilterBarProps['layout']>, string> = {
  flex: 'flex flex-wrap items-end gap-3',
  grid: 'grid auto-rows-min grid-cols-1 items-start gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-4',
};

export function FilterBar({
  children,
  activeFilters,
  onClearAll,
  className,
  variant = 'card',
  layout = 'flex',
}: FilterBarProps) {
  const chips = activeFilters ?? [];
  const hasChips = chips.length > 0;
  const showClearAll = chips.length >= 2 && onClearAll;
  const inner = LAYOUT_CLASSES[layout];

  if (variant === 'inset') {
    return (
      <div className={cn('flex flex-col', className)}>
        <div className={cn(inner, 'px-4 py-3')}>{children}</div>
        {hasChips ? (
          <div className="flex flex-wrap items-center gap-2 border-t border-(--color-border) px-4 py-2">
            {chips.map((f) => (
              <FilterChip key={f.key} label={f.label} onClear={f.onClear} />
            ))}
            {showClearAll ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearAll}
                className="text-meta-sm text-neutral-500 hover:text-neutral-900"
              >
                Clear all
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className={cn(inner, 'bg-surface rounded-lg border border-(--color-border) px-4 py-3')}>
        {children}
      </div>

      {hasChips ? (
        <div className="flex flex-wrap items-center gap-2">
          {chips.map((f) => (
            <FilterChip key={f.key} label={f.label} onClear={f.onClear} />
          ))}
          {showClearAll ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearAll}
              className="text-meta-sm text-neutral-500 hover:text-neutral-900"
            >
              Clear all
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
